import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronDown, Trash2, Upload, Download, Search, QrCode } from "lucide-react";
import { AssetQrDialog } from "@/components/om/AssetQrDialog";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, Pill, HealthRing, statusTone, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";

const BLANK = {
  name: "", category: "Mechanical", sub_category: "", make_model: "", serial_no: "",
  installation_date: "", warranty_expiry: "", useful_life_years: 10, site: "", building: "",
  floor: "", zone: "", responsible_team: "", status: "Operational", parent_id: "", level: "Equipment",
  project_id: "", wbs_activity_id: "", handover_date: "",
};

const TEMPLATE_COLS = ["name", "category", "sub_category", "make_model", "serial_no", "installation_date",
  "warranty_expiry", "useful_life_years", "site", "building", "floor", "zone", "responsible_team", "status", "level"];

export default function OmAssets() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [sel, setSel] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [open, setOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [fStatus, setFStatus] = useState("");

  const load = () => api.get("/om/assets").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/om/meta").then(({ data }) => setMeta(data)); }, []);

  const filtered = useMemo(() => rows.filter((r) =>
    (!q || `${r.code} ${r.name} ${r.site} ${r.zone}`.toLowerCase().includes(q.toLowerCase())) &&
    (!fCat || r.category === fCat) && (!fStatus || r.status === fStatus)), [rows, q, fCat, fStatus]);

  const tree = useMemo(() => {
    const byParent = {};
    filtered.forEach((r) => { (byParent[r.parent_id || "root"] ||= []).push(r); });
    const out = [];
    const walk = (parent, depth) => (byParent[parent] || []).forEach((r) => {
      out.push({ ...r, depth, kids: !!byParent[r.id] });
      if (!collapsed[r.id]) walk(r.id, depth + 1);
    });
    walk("root", 0);
    // Orphans surface when a filter hides the parent.
    filtered.filter((r) => r.parent_id && !filtered.some((x) => x.id === r.parent_id) && !out.some((o) => o.id === r.id))
      .forEach((r) => out.push({ ...r, depth: 0, kids: false }));
    return out;
  }, [filtered, collapsed]);

  const create = async () => {
    try {
      const { data } = await api.post("/om/assets", { ...form, useful_life_years: Number(form.useful_life_years) });
      toast.success(`${data.code} registered`);
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/om/assets/${id}`); toast.success("Asset removed"); setSel(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadTemplate = () => {
    const csv = [TEMPLATE_COLS.join(","), "Chiller CH-02,HVAC,Water-cooled,Carrier / 30XA,SN-1001,2025-01-10,2028-01-10,15,Main site,Block A,L1,Zone 2,O&M Core,Operational,Equipment"].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "plan-sarthi-asset-import-template.csv";
    a.click();
  };

  const importCsv = async (file) => {
    const text = await file.text();
    const [head, ...lines] = text.trim().split(/\r?\n/);
    const cols = head.split(",").map((c) => c.trim());
    const parsed = lines.filter(Boolean).map((l) => Object.fromEntries(l.split(",").map((v, i) => [cols[i], v.trim()])));
    try {
      const { data } = await api.post("/om/assets/bulk", { rows: parsed });
      toast.success(`${data.imported} asset(s) imported`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="om-assets-page">
      <OmHead title="Asset & Equipment Register" subtitle="Plant → System → Sub-system → Equipment → Component, with a live health score per asset.">
        <Button variant="outline" onClick={downloadTemplate} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-asset-template-btn">
          <Download className="w-4 h-4 mr-1.5" /> Template
        </Button>
        <label className="inline-flex items-center h-9 px-3 text-[13px] cursor-pointer border" style={{ borderColor: `${GOLD}66`, color: GOLD, borderRadius: 6 }} data-testid="om-asset-import-label">
          <Upload className="w-4 h-4 mr-1.5" /> Bulk import
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])} data-testid="om-asset-import-input" />
        </label>
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-asset-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Register asset
        </Button>
      </OmHead>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name, location" className="pl-8 w-[240px]" style={FIELD} data-testid="om-asset-search" />
        </div>
        <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-asset-category-filter">
          <option value="">All categories</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-asset-status-filter">
          <option value="">All statuses</option>
          {(meta?.statuses || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Pill tone="gold" testid="om-asset-count">{filtered.length} of {rows.length} assets</Pill>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-3">
        <OmCard testid="om-asset-tree">
          {tree.length === 0 ? <p className="text-[12.5px]" style={{ color: MUTED }}>No assets match. Register one, or load the starter register from the command centre.</p> : (
            <ul>
              {tree.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSel(r)}
                    className="w-full flex items-center gap-2 py-1.5 text-left transition-colors duration-150 hover:bg-white/5"
                    style={{ paddingLeft: 8 + r.depth * 16 }}
                    data-testid={`om-asset-row-${r.code}`}
                  >
                    {r.kids ? (
                      <span onClick={(e) => { e.stopPropagation(); setCollapsed((c) => ({ ...c, [r.id]: !c[r.id] })); }} style={{ color: MUTED }}>
                        {collapsed[r.id] ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </span>
                    ) : <span className="w-3.5" />}
                    <span className="text-[11px] tabular-nums" style={{ color: MUTED }}>{r.code}</span>
                    <span className="text-white text-[13px] flex-1 truncate">{r.name}</span>
                    <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                    <span className="text-[11px] tabular-nums w-8 text-right" style={{ color: r.health >= 80 ? "#00C896" : r.health >= 50 ? "#F59E0B" : "#EF4444" }}>{r.health}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </OmCard>

        <OmCard testid="om-asset-detail">
          {!sel ? <p className="text-[12.5px]" style={{ color: MUTED }}>Select an asset to see its details, health score and handover record.</p> : (
            <div>
              <div className="flex items-start gap-3">
                <HealthRing value={sel.health} />
                <div className="min-w-0">
                  <div className="text-[11px]" style={{ color: MUTED }}>{sel.code} · {sel.level}</div>
                  <h3 className="text-white font-semibold text-[15px] leading-snug">{sel.name}</h3>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Pill tone={statusTone(sel.status)}>{sel.status}</Pill>
                    <Pill tone="gold">{sel.category}</Pill>
                    {sel.open_tickets ? <Pill tone="bad">{sel.open_tickets} open ticket(s)</Pill> : null}
                  </div>
                </div>
              </div>
              <dl className="mt-4 space-y-1.5 text-[12px]">
                {[["Make / model", sel.make_model], ["Serial no.", sel.serial_no], ["Sub-category", sel.sub_category],
                  ["Installed", sel.installation_date], ["Warranty expiry", sel.warranty_expiry],
                  ["Useful life", sel.useful_life_years ? `${sel.useful_life_years} years` : ""],
                  ["Location", [sel.site, sel.building, sel.floor, sel.zone].filter(Boolean).join(" · ")],
                  ["Responsible team", sel.responsible_team], ["Construction handover", sel.handover_date],
                  ["Linked WBS activity", sel.wbs_activity_id], ["Last verified PM", sel.last_pm_at ? new Date(sel.last_pm_at).toLocaleDateString("en-IN") : ""]]
                  .filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} className="flex justify-between gap-3">
                      <dt style={{ color: MUTED }}>{l}</dt>
                      <dd className="text-white text-right">{v}</dd>
                    </div>
                  ))}
              </dl>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={() => setQrOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-asset-qr-btn">
                  <QrCode className="w-3.5 h-3.5 mr-1.5" /> QR tag
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(sel.id)} style={{ borderColor: "#EF444466", color: "#EF4444", background: "transparent" }} data-testid="om-asset-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Decommission & remove
                </Button>
              </div>
            </div>
          )}
        </OmCard>
      </div>

      <AssetQrDialog asset={sel} open={qrOpen} onOpenChange={setQrOpen} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">Register an asset</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Asset name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={FIELD} data-testid="om-asset-name-input" />
            </div>
            {[["category", "Category", meta?.categories], ["level", "Hierarchy level", meta?.levels], ["status", "Status", meta?.statuses]].map(([k, l, opts]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid={`om-asset-${k}-select`}>
                  {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Parent asset</Label>
              <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-asset-parent-select">
                <option value="">— Top level —</option>
                {rows.map((r) => <option key={r.id} value={r.id}>{r.code} {r.name}</option>)}
              </select>
            </div>
            {[["sub_category", "Sub-category"], ["make_model", "Make & model"], ["serial_no", "Serial number"],
              ["installation_date", "Installation date"], ["warranty_expiry", "Warranty expiry"], ["handover_date", "Handover date"],
              ["useful_life_years", "Useful life (years)"], ["site", "Site"], ["building", "Building"],
              ["floor", "Floor"], ["zone", "Zone"], ["responsible_team", "Responsible team"],
              ["wbs_activity_id", "Linked WBS activity id"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <Input
                  type={k.includes("date") || k === "warranty_expiry" ? "date" : "text"}
                  value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  style={FIELD} data-testid={`om-asset-${k}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!form.name} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-asset-save-btn">Register asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
